import React, { useEffect, useRef, useState, useCallback, useId } from 'react';
import { FaPaperPlane, FaTimes } from 'react-icons/fa';
import './EmailModal.css';

/**
 * Reusable email dialog.
 *
 * Props:
 * - open: boolean (controls visibility)
 * - title: string (modal title)
 * - initialTo: string
 * - initialCc: string
 * - initialBcc: string
 * - ccPlaceholder?: string
 * - bccPlaceholder?: string
 * - toName?: string
 * - ccName?: string
 * - bccName?: string
 * - toAutoComplete?: string
 * - ccAutoComplete?: string
 * - bccAutoComplete?: string
 * - initialSubject: string
 * - initialMessage: string
 * - allowCcBcc: boolean (default: false)
 * - onClose: () => void  (called when user cancels/closes)
 * - onSend: ({to, cc, bcc, subject, message, attachmentsSelected}) => Promise<void> | void
 * - confirmText: string (default: "Send Email")
 * - cancelText: string (default: "Cancel")
 * - extraContent: ReactNode (optional custom UI rendered under Message)
 */
export default function EmailModal({
  open,
  title = 'Email',
  initialTo = '',
  initialCc = '',
  initialBcc = '',
  toLabel = 'To',
  toPlaceholder = 'to@example.com',
  allowEmptyTo = false,
  onToChange = null,
  ccPlaceholder = 'cc@example.com',
  bccPlaceholder = 'bcc@example.com',
  toName,
  ccName,
  bccName,
  toAutoComplete,
  ccAutoComplete,
  bccAutoComplete,
  initialSubject = '',
  initialMessage = '',
  allowCcBcc = false,
  // Optional: list of attachments to display for selection
  // Shape: [{ filename: string, date?: string|number|Date, description?: string }]
  attachments = [],
  onClose,
  onSend,
  confirmText = 'Send Email',
  cancelText = 'Cancel',
  extraContent = null,
  showAttachmentsToggle = false,
  attachmentsToggleLabel = 'Show attachments',
  attachmentsToggleChecked = false,
  onAttachmentsToggle = null,
  attachmentsLoading = false,
}) {
  const [to, setTo] = useState(initialTo);
  const [cc, setCc] = useState(initialCc);
  const [bcc, setBcc] = useState(initialBcc);
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState(initialMessage);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const firstInputRef = useRef(null);
  const [attachSel, setAttachSel] = useState(new Set());
  const formId = useId();

  const trimListValue = (value) => String(value || '').trim();

  // sync fields when opened or initial props change
  useEffect(() => {
    if (open) {
      setTo(String(initialTo || ''));
      setCc(String(initialCc || ''));
      setBcc(String(initialBcc || ''));
      setSubject(initialSubject || '');
      setMessage(initialMessage || '');
      setError('');
      // reset attachment selection; it'll populate when attachments load
      try { setAttachSel(new Set()); } catch {}
      // focus the "to" field shortly after mount
      setTimeout(() => { try { firstInputRef.current?.focus(); } catch {} }, 0);
    }
  }, [open, initialTo, initialCc, initialBcc, initialSubject, initialMessage]);

  // When attachments load/change, default-select them without resetting email fields.
  useEffect(() => {
    if (!open) return;
    try {
      const keys = Array.isArray(attachments)
        ? attachments
            .map(a => {
              const key = String(a?.emailKey || '');
              if (key) return key;
              return String(a?.filename || '').split('/').pop();
            })
            .filter(Boolean)
        : [];
      if (!keys.length) return;
      setAttachSel(prev => {
        // Only auto-select when nothing is selected yet (common case: attachments were loaded after opening).
        if (prev && prev.size > 0) return prev;
        return new Set(keys);
      });
    } catch {}
  }, [open, attachments]);

  // close on ESC
  useEffect(() => {
    const handler = (e) => { if (open && e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const validateEmails = (value) => {
    if (!value) return true;
    // allow comma or semicolon separated list
    const parts = String(value).split(/[;,]/).map(s => s.trim()).filter(Boolean);
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return parts.every(p => re.test(p));
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('eml-modal__overlay')) onClose?.();
  };

  const handleSend = useCallback(async () => {
    setError('');
    if (!allowEmptyTo) {
      if (!to || !validateEmails(to)) {
        setError('Please enter a valid recipient email (you can separate multiple with commas or semicolons).');
        return;
      }
    } else if (to && !validateEmails(to)) {
      setError('Please enter a valid email (you can separate multiple with commas or semicolons).');
      return;
    }
    if (cc && !validateEmails(cc)) {
      setError('One or more CC emails look invalid.');
      return;
    }
    if (bcc && !validateEmails(bcc)) {
      setError('One or more BCC emails look invalid.');
      return;
    }
    setSending(true);
    try {
      await onSend?.({
        to: trimListValue(to),
        cc: trimListValue(cc),
        bcc: trimListValue(bcc),
        subject,
        message,
        // Provide selected attachment filenames (basename) to caller
        attachmentsSelected: Array.from(attachSel || new Set()),
      });
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  }, [to, cc, bcc, subject, message, attachSel, onSend, onClose]);

  if (!open) return null;

  return (
    <div
      className="eml-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
      onClick={handleOverlayClick}
    >
      <div className="eml-modal">
        <div className="eml-modal__header">
          <h2 id="email-modal-title">{title}</h2>
          <button
            className="eml-modal__close"
            onClick={() => onClose?.()}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="eml-modal__body">
          {error ? <div className="eml-modal__error" role="alert">{error}</div> : null}

          <form
            id={formId}
            className="eml-form"
            autoComplete="on"
            onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          >
            <label>
              <span>{toLabel}</span>
              <input
                ref={firstInputRef}
                type="text"
                placeholder={toPlaceholder}
                name={toName}
                autoComplete={toAutoComplete}
                value={to}
                onChange={(e) => { const v = e.target.value; setTo(v); onToChange?.(v); }}
              />
            </label>

            {allowCcBcc && (
              <>
                <label>
                  <span>CC</span>
                  <input
                    type="text"
                    placeholder={ccPlaceholder}
                    name={ccName}
                    autoComplete={ccAutoComplete}
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                  />
                </label>
                <label>
                  <span>BCC</span>
                  <input
                    type="text"
                    placeholder={bccPlaceholder}
                    name={bccName}
                    autoComplete={bccAutoComplete}
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                  />
                </label>
              </>
            )}

            <label>
              <span>Subject</span>
              <input
                type="text"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>

          <label>
            <span>Message</span>
            <textarea
              placeholder="Write a message…"
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>

          {extraContent}

            {(showAttachmentsToggle || (Array.isArray(attachments) && attachments.length > 0)) && (
              <div className="eml-attach-block">
                <div className="eml-attach-head">
                  <strong>Attachments</strong>
                  <div className="eml-attach-actions">
                    {showAttachmentsToggle && (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                        <input
                          type="checkbox"
                          checked={!!attachmentsToggleChecked}
                          onChange={(e) => onAttachmentsToggle?.(e.target.checked)}
                        />
                        {attachmentsToggleLabel}
                      </label>
                    )}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setAttachSel(new Set(
                        attachments
                          .map(a => {
                            const key = String(a?.emailKey || '');
                            if (key) return key;
                            return String(a?.filename || '').split('/').pop();
                          })
                          .filter(Boolean)
                      ))}
                      disabled={!!showAttachmentsToggle && (!attachmentsToggleChecked || attachmentsLoading || !attachments.length)}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setAttachSel(new Set())}
                      disabled={!!showAttachmentsToggle && (!attachmentsToggleChecked || attachmentsLoading || !attachments.length)}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                {showAttachmentsToggle && !attachmentsToggleChecked ? (
                  <div className="eml-attach-foot" style={{ textAlign: 'left' }}>
                    Attachments are hidden by default. Check the box to load them if you need to review or include additional files.
                  </div>
                ) : attachmentsLoading ? (
                  <div className="eml-attach-foot" style={{ textAlign: 'left' }}>
                    Loading attachments…
                  </div>
                ) : (Array.isArray(attachments) && attachments.length > 0) ? (
                  <>
                    <div className="eml-attach-tablewrap">
                      <table className="eml-attach-table">
                        <thead>
                          <tr>
                            <th style={{ width: 42 }}>Send</th>
                            <th>Filename</th>
                            <th style={{ width: 180 }}>Date</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attachments.map((a, idx) => {
                            const full = String(a?.filename || '');
                            const name = full.split('/').pop();
                            const key = String(a?.emailKey || '') || name;
                            const checked = attachSel.has(key);
                            const dt = a?.date ? new Date(a.date) : null;
                            const dateText = dt && !isNaN(dt.getTime()) ? dt.toLocaleString() : '';
                            return (
                              <tr key={key + idx}>
                                <td>
                                  <input type="checkbox" checked={checked} onChange={(e) => {
                                    setAttachSel(prev => { const n = new Set(prev); if (e.target.checked) n.add(key); else n.delete(key); return n; });
                                  }} />
                                </td>
                                <td title={full}>{name}</td>
                                <td>{dateText}</td>
                                <td className="nowrap">{a?.description || ''}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="eml-attach-foot">Invoice PDF is always attached. Select any additional files to include.</div>
                  </>
                ) : (
                  <div className="eml-attach-foot" style={{ textAlign: 'left' }}>No attachments found for this invoice.</div>
                )}
              </div>
            )}
          </form>
        </div>

        <div className="eml-modal__footer">
          <button className="btn" type="submit" form={formId} disabled={sending}>
            {sending ? 'Sending…' : (<><FaPaperPlane style={{ marginRight: 6 }} />{confirmText}</>)}
          </button>
          <button className="btn btn-secondary" onClick={() => onClose?.()} disabled={sending}>
            <FaTimes style={{ marginRight: 6 }} />{cancelText}
          </button>
        </div>
      </div>
    </div>
  );
}
